import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Heading from "@/components/tpr/Heading/Heading";
import { BreadcrumbItem, BreadcrumbLink } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useBreadcrumb } from "@/contexts/breadcrumbContext";
import { Search } from "lucide-react";
import HelpCategoryCard from "@/components/help/HelpCategoryCard";
import HelpFAQAccordion from "@/components/help/HelpFAQAccordion";
import HelpSearchResults from "@/components/help/HelpSearchResults";
import { useHelpCategories, useHelpFAQs, useHelpSearch } from "@/hooks/useHelpContent";

const HelpView = () => {
  const navigate = useNavigate();
  const { setBreadcrumb } = useBreadcrumb();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  const { data: categories, isLoading: categoriesLoading } = useHelpCategories();
  const { data: searchResults, isLoading: searchLoading } = useHelpSearch(debouncedQuery);
  const { data: faqs, isLoading: faqsLoading } = useHelpFAQs();

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setBreadcrumb(
      <>
        <BreadcrumbItem className="text-sm text-gray-600 truncate max-w-[125px] md:max-w-none">
          <BreadcrumbLink href="#">Help &amp; Support</BreadcrumbLink>
        </BreadcrumbItem>
      </>,
    );
    return () => setBreadcrumb(null);
  }, [setBreadcrumb]);

  const showSearchResults = debouncedQuery.trim().length >= 2;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  return (
    <div>
      <div className="px-4 min-h-[80vh]">
        <div className="mb-8 mt-8 text-center">
          <Heading title={`Help & Support`} />
          <span className="text-sm text-gray-500">Get the help or support you need.</span>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="w-full bg-gray-50 p-4 rounded-md flex gap-2 mb-6">
          <Input
            id="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search help articles..."
            className="w-full h-12 bg-white text-lg"
          />
          <Button type="submit" className="h-12" disabled={searchLoading}>
            <Search /> {searchLoading ? "Searching..." : "Search"}
          </Button>
        </form>

        {/* Search Results */}
        {showSearchResults && (
          <HelpSearchResults results={searchResults} isLoading={searchLoading} query={debouncedQuery} />
        )}

        {/* Category Cards (hidden when searching) */}
        {!showSearchResults && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6 mb-12">
            {categoriesLoading ? (
              <>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-32" />
                ))}
              </>
            ) : (
              categories?.map((category) => (
                <HelpCategoryCard
                  key={category.id}
                  category={category}
                  onClick={() => navigate(`/help/category/${category.slug}`)}
                />
              ))
            )}
          </div>
        )}

        {/* FAQ Section (hidden when searching) */}
        {!showSearchResults && (
          <div className="mt-12">
            <h2 className="text-2xl font-semibold mb-4">Frequently Asked Questions</h2>
            {faqsLoading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-16" />
                ))}
              </div>
            ) : (
              <HelpFAQAccordion faqs={faqs || []} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpView;
